import { Flex, Typography } from 'antd';
import { useMemo, useState, type FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { TeamModal } from './ui/team-modal/team-modal';
import { useToggle } from '@shared/hooks/use-toggle';
import { useGetTeamMembers } from './hooks/use-get-team-members';
import { useUpdateTeamMember } from './hooks/use-update-team-member';
import { useDeleteTeamMember } from './hooks/use-delete-team-member';
import { useGlobalConfirmModalActions } from '@shared/redux/ducks/global-confirm-modal';
import { TeamFilters } from './ui/team-filters/team-filters';
import { TeamList } from './ui/team-list/team-list';
import { useAppSelector } from '@shared/redux';
import { authSelectors } from '@shared/redux/ducks/auth';
// import { useTransferOwner } from './hooks/use-transfer-owner';
import { useTeamFilters } from './hooks/use-team-filters';

export const Team: FC = () => {
  const { Text } = Typography;
  const { t } = useTranslation('translation', { keyPrefix: 'team' });

  const campaignTitle = useAppSelector(authSelectors.selectCurrentOrganizationInfo)?.title;
  // const selfPermission = useAppSelector(authSelectors.selectSelfPermission);
  const organizationId = useAppSelector(authSelectors.selectCurrentOrganizationId);

  const { state: isEditMode, setFalse: setEditModeFalse, setTrue: setEditModeTrue } = useToggle();

  const { state: isTeamModalOpen, setFalse: closeTeamModal, setTrue: openTeamModal } = useToggle();

  const [currentTeammateId, setCurrentTeammateId] = useState(0);

  const { search, sort, handleSearch, handleSort } = useTeamFilters(organizationId);

  const { teamMembers, isEmpty, isLoading } = useGetTeamMembers({
    organizationId,
    skip: !organizationId,
  });

  const normalizedSearch = search.trim().toLowerCase();
  const filteredTeamMembers = useMemo(() => {
    if (!teamMembers?.length) return teamMembers;
    if (!normalizedSearch) return teamMembers;

    return teamMembers.filter((teamMember) => {
      const fullName = teamMember.user_info?.full_name ?? '';
      const email = teamMember.user_info?.email ?? '';
      const phone = teamMember.user_info?.phone ?? '';
      const haystack = `${fullName} ${email} ${phone}`.toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [teamMembers, normalizedSearch]);
  const isFilteredEmpty = normalizedSearch ? !filteredTeamMembers?.length : isEmpty;

  const { updateTeamMember } = useUpdateTeamMember(Number(organizationId));
  const { deleteTeamMember } = useDeleteTeamMember(Number(organizationId));
  // const { transferOwner } = useTransferOwner(Number(organizationId));

  const { openConfirmModal } = useGlobalConfirmModalActions();

  const selectedTeammate = useMemo(
    () => teamMembers?.find((teamMember) => teamMember.user_info.id === currentTeammateId),
    [currentTeammateId, teamMembers],
  );

  const handleCloseModal = () => {
    closeTeamModal();
    setCurrentTeammateId(0);
  };

  const handleOpenConfirmModal = (id: number) => {
    openConfirmModal({
      title: t('deleteModal.title'),
      text: (
        <Trans
          t={t}
          i18nKey="deleteModal.text"
          values={{
            teammate: teamMembers?.find((teamMember) => teamMember.user_info.id === id)?.user_info
              .full_name,
            team: campaignTitle || '-',
            interpolation: { escapeValue: false },
          }}
          components={{ bold: <Text strong /> }}
        />
      ),
      onConfirm: () => deleteTeamMember(id),
    });
  };

  // const handleTransferOwner = (id: number) => {
  //   openConfirmModal({
  //     title: t('transferOwnerModal.title'),
  //     text: (
  //       <Trans
  //         t={t}
  //         i18nKey="transferOwnerModal.text"
  //         values={{
  //           teammate: teamMembers?.find((teamMember) => teamMember.user_info.id === id)?.user_info
  //             .full_name,
  //           team: campaignTitle || '-',
  //           interpolation: { escapeValue: false },
  //         }}
  //         components={{ bold: <Text strong /> }}
  //       />
  //     ),
  //     onConfirm: () => transferOwner(id),
  //   });
  // };

  return (
    <Flex vertical style={{ height: '100%' }}>
      <TeamFilters
        search={search}
        onChangeSearch={handleSearch}
        onButtonClick={() => {
          setEditModeFalse();
          openTeamModal();
        }}
      />

      <TeamList
        teamMembers={filteredTeamMembers}
        isEmpty={isFilteredEmpty}
        isLoading={isLoading}
        sort={sort}
        onSort={handleSort}
        onEdit={(id) => {
          setEditModeTrue();
          setCurrentTeammateId(id);
          openTeamModal();
        }}
        onDelete={(id) => {
          handleOpenConfirmModal(id);
        }}
      />

      <TeamModal
        organizationId={organizationId}
        isEditMode={isEditMode}
        isOpen={isTeamModalOpen}
        selectedTeammate={selectedTeammate}
        onDelete={() => handleOpenConfirmModal(selectedTeammate?.user_info.id || 0)}
        // onTransferOwner={
        //   selfPermission === 'OWNER'
        //     ? () => handleTransferOwner(selectedTeammate?.user_info.id || 0)
        //     : undefined
        // }
        onSubmit={updateTeamMember}
        onClose={handleCloseModal}
      />
    </Flex>
  );
};
